import React from 'react';
import { connect } from 'react-redux';
import { Link, withRouter } from 'react-router-dom';
import { injectIntl } from 'react-intl';
import SortableTree, { getVisibleNodeCount } from 'react-sortable-tree';
import {
  filter as _filter,
  isEmpty as _isEmpty,
  sortBy as _sortBy,
  forEach as _forEach,
  each as _each,
  cloneDeep as _cloneDeep,
  concat as _concat,
  find as _find,
} from 'lodash';

import { formStatusDispatchHelper, formReset } from 'actions/FormStatusActions';
import { addNotification } from 'actions/NotificationsActions';
import { deleterDispatchHelper } from 'actions/DeleterActions';
import { getNameInCurrentLanguage } from 'helpers/intl';

import Breadcrumb from 'components/layout/Breadcrumb';
import Loader from 'components/layout/Loader';
import SimplePrompt from 'components/layout/SimplePrompt';
import IntlFormInlineSelector from 'components/intlForm/IntlFormInlineSelector';

import List from 'components/List';

import CategoryNew from 'components/modals/CategoryNew';
import ItemNew from "../../../components/modals/ItemNew";

import { ROLE_ERP_MASTER } from 'js/constants';
import may from 'js/security';

import DocMeta from 'components/DocMeta';
import { generateMetaTags } from 'helpers/metaTags';

import Button from "react-bootstrap/lib/Button";


class Categories extends React.Component {
  acceptedItemTypes = ['itemPrint', 'itemWebsite', 'item'];

  constructor(props) {
    super(props);

    this.state = {
      showNew: false,
      showNewItem: false,
      categoriesTree: [],
      initial: true,
      catForNewItem: null,
      pathForNewItem: ',0,',
    };
  }

  showNew = () => {
    this.setState({ showNew: true });
  };

  showNewItem = (cat, catPath) => {
    this.setState({ showNewItem: true, catForNewItem: cat, pathForNewItem: catPath });
  };


  closeNew = () => {
    this.setState({ showNew: false, showNewItem:false });
  };

  getBreadcrumb = () => {
    const {
      intl: { messages },
      appIntl,
    } = this.props;

    return (
      <Breadcrumb
        crumbs={[
          { link: appIntl.urlPrefix + 'account', name: 'Dashboard' },
          { link: appIntl.urlPrefix + 'account/settings', name: messages.settings },
          { link: null, name: messages.categories },
        ]}
        className="p-t-0"
      />
    );
  };

  handleRemoveClick = (e, id, type) => {
    e.preventDefault();
    e.stopPropagation();
    if(type==='cat') {
      const {deleteCategory} = this.props;
      deleteCategory(id);
    }
  };

  componentDidUpdate = prevProps => {
    const {
      intl: { messages },
      appIntl: { urlPrefix, displayIntlForm },
      history,
      list,
      newCategoryFormStatus,
      doNotify,
    } = this.props;
    const { categoriesTree, initial } = this.state;

    // new category was saved
    if (prevProps.newCategoryFormStatus.pending && !newCategoryFormStatus.pending && !newCategoryFormStatus.hasError) {
      doNotify(1, messages.category_saved);
      history.push(urlPrefix + 'account/settings/categories/' + newCategoryFormStatus.response.id);
    }

    // the categories list was loaded, prepare the tree in the state
    if ((prevProps.list.pending) && !list.pending && !list.hasError) {
      if(initial) {
        this.setState({categoriesTree: this.unflatten(_cloneDeep(list.data.list)), initial: false});
      }
    }

    // if the intl form display was changed, we need to recalculate the tree new (because of the title)
    if (prevProps.appIntl.displayIntlForm !== displayIntlForm) {
      this.setState({ categoriesTree: this.unflatten(this.flatten(_cloneDeep(categoriesTree))) });
    }
  };

  unflatten = (array, parent, tree) => {
    const { appIntl } = this.props;

    tree = typeof tree !== 'undefined' ? tree : [];
    parent = typeof parent !== 'undefined' ? parent : { id: 0 };

    let children = _filter(array, child => {
      let lookForPath = parent.id + ',' + child.id + ',';
      if (this.acceptedItemTypes.indexOf(child.type) !== -1) {
        //
        if(!child.path){
          child.path=child.category_path || ',0,';
        }
        lookForPath = parent.id + ',';
      }
      return child.path.lastIndexOf(lookForPath) === child.path.length - lookForPath.length;
    });

    if (!_isEmpty(children)) {
      children = _sortBy(children, ['position']);
      _forEach(children, (child, index) => {
        child.position = index;
        if (this.acceptedItemTypes.indexOf(child.type) !== -1) {
          child.type = 'item';
          let attributeOutput = '';
          _forEach(child.attributes, (attribute, attIndex) => {
            attributeOutput += ' ' + attribute.name[0].name + ': ' + attribute.values[0].value[0].name;
          })
          child.title = (
            <span>
              <i className="fa fa-cube"/>{' '}
              <i className={'fa fa-eye' + (child.is_active ? '' : '-slash')}/>{' '}
              <i className={'fa ' + (child.has_variants ? '' : 'fa-dollar-sign')}/>{' '}
              {typeof child.name === 'object' ? getNameInCurrentLanguage(child.name, appIntl) : child.name}{' '}{'(' + attributeOutput + ' )'}
          </span>
          );
        } else {
          child.type = 'cat';
          child.title = (
            <span><i className="fa fa-map-signs"/>{' '}
              <IntlFormInlineSelector/>
              {getNameInCurrentLanguage(child.name, appIntl)}
          </span>
          );
        }


        // console.log(getNameInCurrentLanguage(child.name, appIntl));
      });
      if (parent.id === 0) {
        tree = children;
      } else {
        parent.children = children;
      }
      _each(children, child => {
        this.unflatten(array, child);
      });
    }

    return tree;
  };

  recalculateAfterChange = (children, parent) => {
    parent = typeof parent !== 'undefined' ? parent : { id: 0, path: ',0,' };

    // calculate the path and position new
    if (!_isEmpty(children)) {
      _forEach(children, (child, index) => {
        child.position = index;
        if (this.acceptedItemTypes.indexOf(child.type) !== -1) {
          child.path = parent.path;
        } else {
          child.path = parent.path + child.id + ',';
          if (!_isEmpty(child.children)) {
            child.children = this.recalculateAfterChange(child.children, child);
          }
        }

      });
    }

    return children;
  };

  flatten = children => {
    let result = [];
    _forEach(children, child => {
      result.push(child);
      if (!_isEmpty(child.children)) {
        result = _concat(result, this.flatten(child.children));
      }
    });

    return result;
  };

  handleTreeChange = tree => {
    // check if the tree really changed! check path and position only!
    const flatNewCategoriesTree = this.flatten(this.recalculateAfterChange(_cloneDeep(tree)));

    const { categoriesTree } = this.state;
    const flatCategoriesTree = this.flatten(_cloneDeep(categoriesTree));

    let changed = false;

    _each(flatNewCategoriesTree, newCategory => {
      const oldCategory = _find(flatCategoriesTree, ['id', newCategory.id]);

      if (
        oldCategory === undefined || // if we didn't find the old category
        oldCategory.path !== newCategory.path || // if the path is different
        oldCategory.position !== newCategory.position // if the position has changed
      ) {
        changed = true;
        return false;
      }
    });

    if (changed) {
      this.setState({ categoriesTree: this.unflatten(flatNewCategoriesTree) }, () => {
        const { onSubmit } = this.props;
        const { categoriesTree: updatedCategoriesTree } = this.state;

        onSubmit({
          categories: this.flatten(_cloneDeep(updatedCategoriesTree)),
        });
      });
    } else {
      this.setState({ categoriesTree: tree });
    }
  };

  render() {
    const {
      intl: { messages },
      appIntl: { urlPrefix },
      list,
      user,
      formStatus,
    } = this.props;
    const { categoriesTree, showNew} = this.state;
    const result =
      list.pending || formStatus.pending ? (
        <Loader />
      ) : (
        <div className="panel">
          <div
            className="panel-body"
            style={{ height: getVisibleNodeCount({ treeData: categoriesTree }) * 62 + 50 + 'px' }}
          >
            <SortableTree
              treeData={categoriesTree}
              onChange={this.handleTreeChange}
              canNodeHaveChildren = {(node) => {
                return node.type !== 'item';
              }}
              generateNodeProps={({ node }) => {
                const { id } = node;
                let detailsUrl = urlPrefix + 'account/settings/categories/' + id;
                const buttons = [
                  <>
                    <Link to={detailsUrl} className="btn-rounded m-r-10 btn btn-sm btn-default">
                      <i className="fa fa-pencil-alt" /> {messages.edit}
                    </Link>

                    {may(ROLE_ERP_MASTER, user.roles) && (
                      <SimplePrompt onSuccess={e => this.handleRemoveClick(e, node.id, node.type)} />
                    )}
                  </>,
                ];

                return { buttons };
              }}
            />
          </div>
        </div>
      );

    return (
      <DocMeta tags={generateMetaTags(messages.meta.categories)}>
        <>
          <List
            listName="categories"
            url="/api/categories.json"
            urlData={{ filters: may('ROLE_ROOT_EMPLOYEE', user.roles) ? {} : { owner: '!null' } }}
            list={list}
            title={messages.categories}
            iconClass="fa fa-map-signs"
            showNew
            newText={messages.add_category}
            newAction={this.showNew}
            showSearch={false}
            breadcrumb={this.getBreadcrumb()}
            showPagination={false}
            resPerPage={9999}
          >
            {result}

            <div id="category-new">
              <CategoryNew show={showNew} close={this.closeNew} />
            </div>
          </List>
        </>
      </DocMeta>
    );
  }
}

const mapStateToProps = state => {
  return {
    appIntl: state.intl,
    list: state.list.categories,
    user: state.login.user,
    formStatus: state.formStatus.categoryTree,
    newCategoryFormStatus: state.formStatus.categoryNew,
  };
};
const mapDispatchToProps = dispatch => {
  return {
    doNotify: (code, text) => {
      dispatch(addNotification(code, text));
    },
    onSubmit: args => {
      formStatusDispatchHelper('categoryTree', '/api/category/tree/update.json', args, 'put', dispatch);
    },
    deleteCategory: categoryId => {
      deleterDispatchHelper('category', '/api/categories/' + categoryId + '.json', dispatch);
    },
  };
};

export default withRouter(
  injectIntl(
    connect(
      mapStateToProps,
      mapDispatchToProps
    )(Categories)
  )
);
